
import React, {useState, useEffect} from 'react'
import {Table, Form, Button, Spinner, Badge, Dropdown, ButtonGroup, InputGroup, Modal} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_RECIPIENTS} from '../../../gql/queries/contactsApiQueries'
import {REMOVE_FROM_RECIPIENTS} from '../../../gql/mutations/recipients'
import {BULK_UNSUBSCRIBE_CONTACTS} from '../../../gql/mutations/subscription'
import {IResRecipientsAPI} from '../../../types/contactsApi'
import {RecipientFilters as FilterTypes, RecipientStats} from '../recipientsTypes'
import {PaginationSection} from '../../shared/PaginationSection'
import {PaginationUtils} from '../../shared/PaginationUtils'
import {BulkTagModal} from './BulkTagModal'
import {ModalPreview} from '../../contacts/components/ModalPreview'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {getTagContrastColor} from '../../../helpers'
import {toast} from 'react-toastify'
import {IntlShape, MessageDescriptor, useIntl, FormattedMessage} from 'react-intl'
import {
  RECIPIENT_MESSAGES as M,
  POLICY_FILTER_MESSAGES as P,
  CROSS_MARK,
  EM_DASH,
  dateRange,
  fullName,
  overflowSuffix,
  translatedCatalogs,
} from '../recipientMessages'
import './RecipientList.css'

interface StateOption { id: string; name: string; code: string }
interface CarrierOption { id: string; name: string }

interface FilterChip { key: string; label: string; onRemove: () => void }

/**
 * Etiquetas de estado de póliza y de jurisdicción: son enums servidos por el
 * backend (`allPolicyStatuses`) y acrónimos, así que no se traducen (§10 del
 * project-plan; el modal de filtros también muestra el título del backend).
 */
const POLICY_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active', EXPIRED: 'Expired', CANCELLED: 'Cancelled', PENDING: 'Pending',
  CANCELED: 'Canceled', RENEWED: 'Renewed',
}
const JURISDICTION_LABELS: Record<string, string> = {
  FFM: 'FFM', SBM: 'SBM', EXPANDED_MEDICAID: 'Expanded Medicaid',
}

function buildFilterChips(
  intl: IntlShape,
  filters: FilterTypes,
  clear: (patch: Partial<FilterTypes>) => void,
  states: StateOption[],
  carriers: CarrierOption[],
): FilterChip[] {
  const chips: FilterChip[] = []
  const catalogs = translatedCatalogs(intl)

  /** `Carrier: Aetna, Cigna +3` — el label sale de la misma llave que usa el modal (§17.13). */
  const chip = (key: string, label: MessageDescriptor, value: string, onRemove: () => void) =>
    chips.push({
      key,
      label: intl.formatMessage(M.chipValue, {label: intl.formatMessage(label), value}),
      onRemove,
    })
  /** `Agent (3)` — cuando la lista es demasiado larga para enumerarla. */
  const chipCount = (key: string, label: MessageDescriptor, count: number, onRemove: () => void) =>
    chips.push({
      key,
      label: intl.formatMessage(M.chipCount, {label: intl.formatMessage(label), count}),
      onRemove,
    })

  const join = (labels: string[], limit = 2): string =>
    labels.length <= limit
      ? labels.join(', ')
      : labels.slice(0, limit).join(', ') + overflowSuffix(labels.length - limit)

  const fromOptions = (values: string[], opts: {value: string; label: string}[], limit = 2) =>
    join(values.map(v => opts.find(o => o.value === v)?.label || v), limit)

  const fromNamed = <T extends {id: string; name: string}>(ids: string[], list: T[], limit = 2) =>
    join(ids.map(id => list.find(i => i.id === id)?.name || id), limit)

  const boolLabel = (v: boolean | undefined) =>
    intl.formatMessage(v === true ? P.yes : P.no)

  if (filters.state.length > 0) {
    const stateLabels = filters.state.map(id => states.find(s => s.id === id)?.code || id)
    chip('state', P.state, join(stateLabels, 3), () => clear({ state: [] }))
  }
  if (filters.carrier.length > 0) {
    chip('carrier', P.carrier, fromNamed(filters.carrier, carriers), () => clear({ carrier: [] }))
  }
  if (filters.policyStatus.length > 0) {
    const labels = filters.policyStatus.map(v => POLICY_STATUS_LABELS[v] || v)
    chip('policyStatus', P.policyStatus, labels.join(', '), () => clear({ policyStatus: [] }))
  }
  if (filters.policyType.length > 0) {
    chip('policyType', P.policyTypes, filters.policyType.join(', '), () => clear({ policyType: [] }))
  }
  if (filters.isPolicyHolder !== undefined) {
    chip('isPolicyHolder', M.chipPolicyHolder, boolLabel(filters.isPolicyHolder), () => clear({ isPolicyHolder: undefined }))
  }
  if (filters.withoutPolicy !== undefined) {
    chip('withoutPolicy', M.chipWithoutPolicy, boolLabel(filters.withoutPolicy), () => clear({ withoutPolicy: undefined }))
  }
  if (filters.unassigned !== undefined) {
    chip('unassigned', P.unassignedPolicies, boolLabel(filters.unassigned), () => clear({ unassigned: undefined }))
  }
  if (filters.user.length > 0) {
    chipCount('user', P.agent, filters.user.length, () => clear({ user: [] }))
  }
  if (filters.assignedTo.length > 0) {
    chipCount('assignedTo', P.assignedTo, filters.assignedTo.length, () => clear({ assignedTo: [] }))
  }
  if (filters.stateJurisdictionType) {
    const label = JURISDICTION_LABELS[filters.stateJurisdictionType] || filters.stateJurisdictionType
    chip('stateJurisdictionType', P.stateJurisdictionType, label, () => clear({ stateJurisdictionType: '' }))
  }
  if (filters.zipCode) {
    chip('zipCode', P.zipCode, filters.zipCode, () => clear({ zipCode: '' }))
  }
  if (filters.immigrationStatus.length > 0) {
    chip('immigrationStatus', P.immigrationStatus, fromOptions(filters.immigrationStatus, catalogs.immigration), () => clear({ immigrationStatus: [] }))
  }
  if (filters.documentStatus.length > 0) {
    chip('documentStatus', P.documentStatus, fromOptions(filters.documentStatus, catalogs.document), () => clear({ documentStatus: [] }))
  }
  if (filters.paymentStatus.length > 0) {
    chip('paymentStatus', P.paymentStatus, fromOptions(filters.paymentStatus, catalogs.payment), () => clear({ paymentStatus: [] }))
  }
  if (filters.leadSource.length > 0) {
    chipCount('leadSource', P.source, filters.leadSource.length, () => clear({ leadSource: [] }))
  }
  if (filters.metalLevel) {
    const label = catalogs.metal.find(o => o.value === filters.metalLevel)?.label || filters.metalLevel
    chip('metalLevel', P.metalLevel, label, () => clear({ metalLevel: '' }))
  }
  if (filters.holderGender) {
    const label = catalogs.gender.find(o => o.value === filters.holderGender)?.label || filters.holderGender
    chip('holderGender', P.genderHolder, label, () => clear({ holderGender: '' }))
  }
  if (filters.hasMemberId !== undefined) {
    chip('hasMemberId', P.memberIdStatus, boolLabel(filters.hasMemberId), () => clear({ hasMemberId: undefined }))
  }
  if (filters.commissionPayable !== undefined) {
    chip('commissionPayable', P.commissionReportStatus, boolLabel(filters.commissionPayable), () => clear({ commissionPayable: undefined }))
  }
  if (filters.commissionPaymentStatus) {
    const label = catalogs.commissionStatus.find(o => o.value === filters.commissionPaymentStatus)?.label || filters.commissionPaymentStatus
    chip('commissionPaymentStatus', P.commissionPaymentStatus, label, () => clear({ commissionPaymentStatus: '' }))
  }
  if (filters.ownsCar !== undefined) {
    chip('ownsCar', P.ownsCar, boolLabel(filters.ownsCar), () => clear({ ownsCar: undefined }))
  }
  if (filters.ownsHome !== undefined) {
    chip('ownsHome', P.ownsHome, boolLabel(filters.ownsHome), () => clear({ ownsHome: undefined }))
  }
  if (filters.hasAutoPolicy !== undefined) {
    chip('hasAutoPolicy', P.hasAutoPolicy, boolLabel(filters.hasAutoPolicy), () => clear({ hasAutoPolicy: undefined }))
  }
  if (filters.hasHomePolicy !== undefined) {
    chip('hasHomePolicy', P.hasHomePolicy, boolLabel(filters.hasHomePolicy), () => clear({ hasHomePolicy: undefined }))
  }
  if (filters.policyCreatedFrom || filters.policyCreatedTo) {
    chip('policyCreated', P.createdDate, dateRange(filters.policyCreatedFrom, filters.policyCreatedTo), () => clear({ policyCreatedFrom: '', policyCreatedTo: '' }))
  }
  if (filters.submissionDateFrom || filters.submissionDateTo) {
    chip('submissionDate', M.submissionDate, dateRange(filters.submissionDateFrom, filters.submissionDateTo), () => clear({ submissionDateFrom: '', submissionDateTo: '' }))
  }
  if (filters.carrierStatusGeneric.length > 0) {
    chip('carrierStatus', P.carrierStatus, fromOptions(filters.carrierStatusGeneric, catalogs.carrierStatus, filters.carrierStatusGeneric.length), () => clear({ carrierStatusGeneric: [] }))
  }
  if (filters.carrierLastAsOfDateAfter || filters.carrierLastAsOfDateBefore) {
    chip('carrierAsOf', P.carrierAsOfDate, dateRange(filters.carrierLastAsOfDateAfter, filters.carrierLastAsOfDateBefore), () => clear({ carrierLastAsOfDateAfter: '', carrierLastAsOfDateBefore: '' }))
  }
  if (filters.carrierIsAgeIn !== undefined) {
    chip('carrierIsAgeIn', P.ageIn, boolLabel(filters.carrierIsAgeIn), () => clear({ carrierIsAgeIn: undefined }))
  }
  if (filters.carrierIsDelinquent !== undefined) {
    chip('carrierIsDelinquent', P.delinquent, boolLabel(filters.carrierIsDelinquent), () => clear({ carrierIsDelinquent: undefined }))
  }
  if (filters.carrierIsUnpaidPending !== undefined) {
    chip('carrierIsUnpaidPending', P.unpaidPending, boolLabel(filters.carrierIsUnpaidPending), () => clear({ carrierIsUnpaidPending: undefined }))
  }
  if (filters.carrierIsUnpaidPastDue !== undefined) {
    chip('carrierIsUnpaidPastDue', P.unpaidPastDue, boolLabel(filters.carrierIsUnpaidPastDue), () => clear({ carrierIsUnpaidPastDue: undefined }))
  }

  return chips
}

interface RecipientListProps {
  filters: FilterTypes
  setFilters: React.Dispatch<React.SetStateAction<FilterTypes>>
  selectedContactIds: string[]
  onSelectionChange: (ids: string[]) => void
  segmentId?: string | null
  onStatsChange?: (stats: RecipientStats) => void
  onOpenFilters: () => void
  activeFilterCount: number
  states?: StateOption[]
  carriers?: CarrierOption[]
}

export const RecipientList: React.FC<RecipientListProps> = ({
  filters,
  setFilters,
  selectedContactIds,
  onSelectionChange,
  segmentId,
  onStatsChange,
  onOpenFilters,
  activeFilterCount,
  states = [],
  carriers = [],
}) => {
  const intl = useIntl()
  const [searchTerm, setSearchTerm] = useState(filters.search)
  const [first, setFirst] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search)
  const [showBulkTagModal, setShowBulkTagModal] = useState(false)
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null)
  const [previewContactId, setPreviewContactId] = useState<string | null>(null)
  const [tagTargetId, setTagTargetId] = useState<string | null>(null)

  const [getContacts, {data, loading}] = useLazyQuery<IResRecipientsAPI>(GET_ALL_RECIPIENTS, {
    fetchPolicy: 'network-only',
  })

  const [removeFromRecipients, {loading: removing}] = useMutation(REMOVE_FROM_RECIPIENTS)
  const [bulkUnsubscribe, {loading: unsubscribing}] = useMutation(BULK_UNSUBSCRIBE_CONTACTS)

  const clearFilter = (patch: Partial<FilterTypes>) => {
    setFilters(prev => ({...prev, ...patch}))
  }

  const filterChips = buildFilterChips(intl, filters, clearFilter, states, carriers)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to page 1 when advanced filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [
    filters.status, segmentId, filters.tags, filters.effectiveYears,
    filters.state, filters.carrier, filters.policyStatus, filters.policyType,
    filters.isPolicyHolder, filters.withoutPolicy, filters.unassigned,
    filters.user, filters.assignedTo, filters.stateJurisdictionType, filters.zipCode,
    filters.immigrationStatus, filters.documentStatus, filters.paymentStatus, filters.leadSource,
    filters.metalLevel, filters.holderGender, filters.hasMemberId,
    filters.commissionPayable, filters.commissionPaymentStatus,
    filters.ownsCar, filters.ownsHome, filters.hasAutoPolicy, filters.hasHomePolicy,
    filters.policyCreatedFrom, filters.policyCreatedTo, filters.submissionDateFrom, filters.submissionDateTo,
    filters.carrierStatusGeneric, filters.carrierLastAsOfDateAfter, filters.carrierLastAsOfDateBefore,
    filters.carrierIsAgeIn, filters.carrierIsDelinquent, filters.carrierIsUnpaidPending, filters.carrierIsUnpaidPastDue,
  ])

  const buildQueryVars = (overrides?: Record<string, any>) => ({
    name: debouncedSearch || undefined,
    first,
    offset: (currentPage - 1) * first,
    status: filters.status !== 'ALL' ? filters.status : undefined,
    segment: segmentId || undefined,
    tags: filters.tags && filters.tags.length > 0 ? filters.tags.join(',') : undefined,
    effectiveYear: filters.effectiveYears && filters.effectiveYears.length > 0 ? filters.effectiveYears.join(',') : undefined,
    state: filters.state && filters.state.length > 0 ? filters.state.join(',') : undefined,
    carrier: filters.carrier && filters.carrier.length > 0 ? filters.carrier.join(',') : undefined,
    policyStatus: filters.policyStatus && filters.policyStatus.length > 0 ? filters.policyStatus.join(',') : undefined,
    policyType: filters.policyType && filters.policyType.length > 0 ? filters.policyType.join(',') : undefined,
    isPolicyHolder: filters.isPolicyHolder,
    withoutPolicy: filters.withoutPolicy,
    unassigned: filters.unassigned,
    user: filters.user && filters.user.length > 0 ? filters.user.join(',') : undefined,
    assignedTo: filters.assignedTo && filters.assignedTo.length > 0 ? filters.assignedTo.join(',') : undefined,
    stateJurisdictionType: filters.stateJurisdictionType || undefined,
    zipCode: filters.zipCode || undefined,
    immigrationStatus: filters.immigrationStatus && filters.immigrationStatus.length > 0 ? filters.immigrationStatus.join(',') : undefined,
    documentStatus: filters.documentStatus && filters.documentStatus.length > 0 ? filters.documentStatus.join(',') : undefined,
    paymentStatus: filters.paymentStatus && filters.paymentStatus.length > 0 ? filters.paymentStatus.join(',') : undefined,
    leadSource: filters.leadSource && filters.leadSource.length > 0 ? filters.leadSource.join(',') : undefined,
    metalLevel: filters.metalLevel || undefined,
    gender: filters.holderGender || undefined,
    hasMemberId: filters.hasMemberId,
    commissionPayable: filters.commissionPayable,
    commissionPaymentStatus: filters.commissionPaymentStatus || undefined,
    ownsCar: filters.ownsCar,
    ownsHome: filters.ownsHome,
    hasAutoPolicy: filters.hasAutoPolicy,
    hasHomePolicy: filters.hasHomePolicy,
    policyCreatedFrom: filters.policyCreatedFrom || undefined,
    policyCreatedTo: filters.policyCreatedTo || undefined,
    submissionDateFrom: filters.submissionDateFrom || undefined,
    submissionDateTo: filters.submissionDateTo || undefined,
    carrierStatusGeneric: filters.carrierStatusGeneric && filters.carrierStatusGeneric.length > 0 ? filters.carrierStatusGeneric.join(',') : undefined,
    carrierLastAsOfDateAfter: filters.carrierLastAsOfDateAfter || undefined,
    carrierLastAsOfDateBefore: filters.carrierLastAsOfDateBefore || undefined,
    carrierIsAgeIn: filters.carrierIsAgeIn,
    carrierIsDelinquent: filters.carrierIsDelinquent,
    carrierIsUnpaidPending: filters.carrierIsUnpaidPending,
    carrierIsUnpaidPastDue: filters.carrierIsUnpaidPastDue,
    ...overrides,
  })

  // Fetch contacts when filters or search change
  useEffect(() => {
    getContacts({ variables: buildQueryVars() })
  }, [debouncedSearch, currentPage, first, filters.status, segmentId, filters.tags, filters.effectiveYears,
    filters.state, filters.carrier, filters.policyStatus, filters.policyType,
    filters.isPolicyHolder, filters.withoutPolicy, filters.unassigned,
    filters.user, filters.assignedTo, filters.stateJurisdictionType, filters.zipCode,
    filters.immigrationStatus, filters.documentStatus, filters.paymentStatus, filters.leadSource,
    filters.metalLevel, filters.holderGender, filters.hasMemberId,
    filters.commissionPayable, filters.commissionPaymentStatus,
    filters.ownsCar, filters.ownsHome, filters.hasAutoPolicy, filters.hasHomePolicy,
    filters.policyCreatedFrom, filters.policyCreatedTo, filters.submissionDateFrom, filters.submissionDateTo,
    filters.carrierStatusGeneric, filters.carrierLastAsOfDateAfter, filters.carrierLastAsOfDateBefore,
    filters.carrierIsAgeIn, filters.carrierIsDelinquent, filters.carrierIsUnpaidPending, filters.carrierIsUnpaidPastDue,
    getContacts]) // eslint-disable-line react-hooks/exhaustive-deps

  const contacts = data?.allRecipients?.edges || []
  const totalCount = data?.allRecipients?.totalCount || 0
  const stats = data?.recipientStats

  // Pass stats to parent
  useEffect(() => {
    if (stats && onStatsChange) {
      onStatsChange({
        total: stats.totalRecipients,
        subscribed: stats.subscribedCount,
        unsubscribed: stats.unsubscribedCount,
      })
    }
  }, [stats, onStatsChange])
  const totalPages = Math.ceil(totalCount / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalCount)

  const isAllSelected = contacts.length > 0 && contacts.every(edge => selectedContactIds.includes(edge.node.id))

  const toggleSelectAll = () => {
    if (isAllSelected) {
      const currentIds = contacts.map(edge => edge.node.id)
      onSelectionChange(selectedContactIds.filter(id => !currentIds.includes(id)))
    } else {
      const currentIds = contacts.map(edge => edge.node.id)
      const combined = [...selectedContactIds, ...currentIds]
      const unique = combined.filter((id, index) => combined.indexOf(id) === index)
      onSelectionChange(unique)
    }
  }

  const toggleSelect = (contactId: string) => {
    if (selectedContactIds.includes(contactId)) {
      onSelectionChange(selectedContactIds.filter(id => id !== contactId))
    } else {
      onSelectionChange([...selectedContactIds, contactId])
    }
  }

  const handleBulkDelete = async () => {
    if (!window.confirm(intl.formatMessage(M.confirmBulkDelete, {count: selectedContactIds.length}))) {
      return
    }
    // TODO: Implement delete mutation
    alert(intl.formatMessage(M.bulkDeleteNotImplemented))
  }

  const handleRefresh = () => {
    getContacts({ variables: buildQueryVars() })
  }

  const handleSearch = () => {
    setCurrentPage(1)
    setDebouncedSearch(searchTerm)
  }

  const handleRemoveFromRecipients = async () => {
    if (!window.confirm(intl.formatMessage(M.confirmRemove, {count: selectedContactIds.length}))) {
      return
    }

    try {
      const result = await removeFromRecipients({
        variables: {contactIds: selectedContactIds},
      })
      toast.success(
        intl.formatMessage(M.toastRemoved, {
          count: result.data.removeFromRecipients.removedCount,
        })
      )
      onSelectionChange([])
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorRemove))
    }
  }

  const handleBulkUnsubscribe = async () => {
    if (!window.confirm(intl.formatMessage(M.confirmBulkUnsubscribe, {count: selectedContactIds.length}))) {
      return
    }

    try {
      const result = await bulkUnsubscribe({
        variables: {
          contactIds: selectedContactIds,
          reason: 'Bulk unsubscribe from admin panel',
        },
      })
      toast.success(
        intl.formatMessage(M.toastUnsubscribedCount, {
          count: result.data.bulkUnsubscribeContacts.unsubscribedCount,
        })
      )
      onSelectionChange([])
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorUnsubscribeBulk))
    }
  }

  const confirmUnsubscribe = (contactId: string) => {
    setConfirmTargetId(contactId)
  }

  const executeUnsubscribe = async () => {
    if (!confirmTargetId) return
    try {
      await bulkUnsubscribe({
        variables: {
          contactIds: [confirmTargetId],
          reason: 'Unsubscribed from recipients list',
        },
      })
      toast.success(intl.formatMessage(M.toastUnsubscribedOne))
      onSelectionChange(selectedContactIds.filter((id) => id !== confirmTargetId))
      setConfirmTargetId(null)
      handleRefresh()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorUnsubscribeOne))
    }
  }



  return (
    <div className='recipient-list'>
      {/* Header with Search */}
      <div className='list-header mb-4'>
        <InputGroup className='recipients-search-toolbar'>
          <InputGroup.Text>
            <i className='bi bi-search'></i>
          </InputGroup.Text>
          <Form.Control
            type='text'
            placeholder={intl.formatMessage(M.searchPlaceholder)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearch()
              }
            }}
          />
          <Button
            variant='outline-success'
            style={{border: '1px solid #dbdfe9'}}
            onClick={handleSearch}
          >
            <FormattedMessage {...M.search} />
          </Button>
          <Button
            variant='outline-primary'
            style={{border: '1px solid #dbdfe9'}}
            onClick={onOpenFilters}
          >
            <FormattedMessage {...M.filter} />
            {activeFilterCount > 0 && (
              <Badge bg='light' text='primary' pill className='ms-2'>{activeFilterCount}</Badge>
            )}
          </Button>
        </InputGroup>
      </div>

      {/* Active filter chips */}
      {filterChips.length > 0 && (
        <div className='filter-chips-bar mb-3'>
          {filterChips.map(chip => (
            <span key={chip.key} className='filter-chip'>
              {chip.label}
              <button
                type='button'
                className='filter-chip-remove'
                onClick={chip.onRemove}
                aria-label={intl.formatMessage(P.removeFilter, {label: chip.label})}
              >
                {CROSS_MARK}
              </button>
            </span>
          ))}
          <button
            type='button'
            className='btn btn-link btn-sm p-0 ms-1 text-danger'
            onClick={() => setFilters(prev => ({
              ...prev,
              state: [], carrier: [], policyStatus: [], policyType: [],
              isPolicyHolder: undefined, withoutPolicy: undefined, unassigned: undefined,
              user: [], assignedTo: [], stateJurisdictionType: '', zipCode: '',
              immigrationStatus: [], documentStatus: [], paymentStatus: [], leadSource: [],
              metalLevel: '', holderGender: '', hasMemberId: undefined,
              commissionPayable: undefined, commissionPaymentStatus: '',
              ownsCar: undefined, ownsHome: undefined, hasAutoPolicy: undefined, hasHomePolicy: undefined,
              policyCreatedFrom: '', policyCreatedTo: '', submissionDateFrom: '', submissionDateTo: '',
              carrierStatusGeneric: [], carrierLastAsOfDateAfter: '', carrierLastAsOfDateBefore: '',
              carrierIsAgeIn: undefined, carrierIsDelinquent: undefined,
              carrierIsUnpaidPending: undefined, carrierIsUnpaidPastDue: undefined,
            }))}
          >
            <FormattedMessage {...M.clearAll} />
          </button>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedContactIds.length > 0 && (
        <div className='bulk-actions-bar mb-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <span className='text-muted'>
              <FormattedMessage
                {...M.contactsSelected}
                values={{
                  count: selectedContactIds.length,
                  b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                }}
              />
            </span>
            <div className='d-flex gap-2'>
              <Button variant='outline-primary' size='sm' onClick={() => setShowBulkTagModal(true)}>
                <i className='bi bi-tags me-1'></i>
                <FormattedMessage {...M.manageTags} />
              </Button>
              <Button variant='outline-danger' size='sm' onClick={handleBulkDelete}>
                <i className='bi bi-trash me-1'></i>
                <FormattedMessage {...M.delete} />
              </Button>
              <Button variant='outline-warning' size='sm' onClick={handleRemoveFromRecipients} disabled={removing}>
                <i className='bi bi-person-dash me-1'></i>
                {intl.formatMessage(removing ? M.removing : M.removeFromRecipients)}
              </Button>
              <Button variant='outline-danger' size='sm' onClick={handleBulkUnsubscribe} disabled={unsubscribing}>
                <i className='bi bi-envelope-slash me-1'></i>
                {intl.formatMessage(unsubscribing ? M.unsubscribing : M.bulkUnsubscribe)}
              </Button>
              <Button variant='outline-secondary' size='sm' onClick={() => onSelectionChange([])}>
                <i className='bi bi-x me-1'></i>
                <FormattedMessage {...M.clearSelection} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className='results-header'>
        <PaginationSection
          first={first}
          setFirst={(value) => {
            setFirst(value)
            setCurrentPage(1)
          }}
          currentPage={currentPage}
          totalPages={totalPages}
          start={start}
          end={end}
          totalPolicies={totalCount}
          updatePage={setCurrentPage}
          PaginationUtils={PaginationUtils}
        />
      </div>

      <div className='table-container'>
        {loading && contacts.length === 0 ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : contacts.length === 0 ? (
          <div className='text-center py-5 text-muted'>
            <i className='bi bi-inbox fs-1 d-block mb-2'></i>
            <p><FormattedMessage {...M.listEmpty} /></p>
          </div>
        ) : (
          <Table hover responsive>
            <thead>
              <tr>
                <th style={{width: '50px'}}>
                  <Form.Check
                    type='checkbox'
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th><FormattedMessage {...M.name} /></th>
                <th><FormattedMessage {...M.email} /></th>
                <th><FormattedMessage {...M.phone} /></th>
                <th><FormattedMessage {...M.tags} /></th>
                <th><FormattedMessage {...M.status} /></th>
                <th style={{width: '100px'}}><FormattedMessage {...M.actions} /></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(({node: contact}) => (
                <tr key={contact.id}>
                  <td>
                    <Form.Check
                      type='checkbox'
                      checked={selectedContactIds.includes(contact.id)}
                      onChange={() => toggleSelect(contact.id)}
                    />
                  </td>
                  <td>
                    <strong>{contact.fullName || fullName(contact.firstName, contact.lastName)}</strong>
                  </td>
                  <td>{contact.email || <span className='text-muted'>{EM_DASH}</span>}</td>
                  <td>
                    <PhoneProviderActions phone={contact.phone} emptyText={EM_DASH} />
                  </td>
                  <td>
                    {contact.tagAssignments && contact.tagAssignments.length > 0 ? (
                      <div className='d-flex flex-wrap gap-1'>
                        {contact.tagAssignments.map((assignment) => (
                          <span
                            key={assignment.tag.id}
                            className='badge rounded-pill'
                            style={{
                              backgroundColor: assignment.tag.color,
                              color: getTagContrastColor(assignment.tag.color),
                              fontSize: '0.7rem',
                            }}
                          >
                            {assignment.tag.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className='text-muted small'><FormattedMessage {...M.noTags} /></span>
                    )}
                  </td>
                  <td>
                    {contact.subscriptionStatus === 'SUBSCRIBED' ? (
                      <Badge bg='success' className='subscription-badge'>
                        <i className='bi bi-check-circle me-1'></i>
                        <FormattedMessage {...M.subscribed} />
                      </Badge>
                    ) : (
                      <Badge bg='danger' className='subscription-badge'>
                        <i className='bi bi-x-circle me-1'></i>
                        <FormattedMessage {...M.unsubscribed} />
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Dropdown as={ButtonGroup} size='sm'>
                      <Button variant='outline-primary' size='sm'>
                        <FormattedMessage {...M.view} />
                      </Button>
                      <Dropdown.Toggle split variant='outline-primary' size='sm' />
                      <Dropdown.Menu>
                          <Dropdown.Item onClick={() => setPreviewContactId(contact.id)}>
                          <i className='bi bi-eye me-2'></i>
                          <FormattedMessage {...M.view} />
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setTagTargetId(contact.id)}>
                          <i className='bi bi-tags me-2'></i>
                          <FormattedMessage {...M.manageTags} />
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          className='text-danger'
                          onClick={() => confirmUnsubscribe(contact.id)}
                          disabled={unsubscribing}
                        >
                          <i className='bi bi-x-circle me-2'></i>
                          {intl.formatMessage(unsubscribing ? M.unsubscribing : M.unsubscribe)}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Bulk Tag Modal */}
      <BulkTagModal
        show={showBulkTagModal}
        onHide={() => setShowBulkTagModal(false)}
        selectedContactIds={selectedContactIds}
        onSuccess={() => {
          handleRefresh()
          onSelectionChange([])
        }}
      />

      {/* Per-row Tag Modal */}
      {tagTargetId && (
        <BulkTagModal
          show={Boolean(tagTargetId)}
          onHide={() => setTagTargetId(null)}
          selectedContactIds={[tagTargetId]}
          onSuccess={() => {
            setTagTargetId(null)
            handleRefresh()
          }}
        />
      )}

      {/* Contact Preview Modal */}
      {previewContactId && (
        <ModalPreview idContact={previewContactId} onClose={() => setPreviewContactId(null)} />
      )}

      <Modal show={Boolean(confirmTargetId)} onHide={() => setConfirmTargetId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title><FormattedMessage {...M.confirmUnsubscribeTitle} /></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormattedMessage {...M.confirmUnsubscribeBody} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setConfirmTargetId(null)} disabled={unsubscribing}>
            <FormattedMessage {...M.cancel} />
          </Button>
          <Button variant='danger' onClick={executeUnsubscribe} disabled={unsubscribing}>
            {intl.formatMessage(unsubscribing ? M.unsubscribing : M.unsubscribe)}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
